/**
 * Badge applicatif PWA — petit chiffre rouge sur l'icône home screen.
 *
 * Supporté depuis iOS 16.4 Safari + Chromium récents (Badging API). Sur
 * navigateurs sans support, no-op silencieux.
 *
 * Le compteur courant est persisté dans IDB pour que le SW (push handler)
 * puisse l'incrémenter sans connaître la valeur actuelle (l'API n'a pas
 * de "get"). Le page-side reset à la valeur exacte (= count INBOX UNREAD)
 * après chaque sync, ce qui corrige toute dérive accumulée.
 *
 * Module pur — peut être importé du SW comme de la page (uses `navigator`
 * qui est global dans les deux contextes).
 */

import { get, set } from "idb-keyval";

const BADGE_KEY = "mailfox:badge:count";

interface BadgingNavigator {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

function badgeNav(): BadgingNavigator {
  return navigator as unknown as BadgingNavigator;
}

async function readStored(): Promise<number> {
  const v = await get<number>(BADGE_KEY);
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0;
}

async function applyBadge(n: number): Promise<void> {
  const nav = badgeNav();
  try {
    if (n <= 0) {
      await nav.clearAppBadge?.();
    } else {
      await nav.setAppBadge?.(n);
    }
  } catch (e) {
    // Permission Web App manifest manquante / pas de support — best-effort
    console.debug("badge update ignored:", e);
  }
}

/**
 * Set la valeur absolue du badge (= unread count). Page-side, après
 * loadInbox — corrige toute dérive vs incrementBadge SW-side.
 */
export async function setBadge(count: number): Promise<void> {
  const c = Math.max(0, Math.floor(count));
  await set(BADGE_KEY, c);
  await applyBadge(c);
}

/**
 * Incrémente le badge. SW-side, après confirmed messagesAdded — permet à
 * l'utilisateur de voir le compteur monter sur son lock screen sans qu'on
 * ait besoin de connaître l'unread count exact (qui demanderait un fetch
 * Gmail supplémentaire).
 */
export async function incrementBadge(delta = 1): Promise<void> {
  const next = (await readStored()) + Math.max(1, delta);
  await set(BADGE_KEY, next);
  await applyBadge(next);
}

/** Reset à 0 + clear icône. Utilisé au sign-out. */
export async function clearBadge(): Promise<void> {
  await set(BADGE_KEY, 0);
  await applyBadge(0);
}
